import { Injectable, computed, signal } from '@angular/core';
import { AccessTokenPayload, decodeAccessToken } from '../models/access-token.model';
import { Customer } from '../models/customer.model';
import { environment } from '../../../environments/environment';

const STORAGE_KEYS = {
  token: 'token',
  customer: 'customer',
  customers: 'customers',
  language: 'language',
  authMeans: 'auth-means',
} as const;

const SESSION_STORAGE_KEYS = {
  passwordExpiryNoticeShown: 'password-expiry-notice-shown',
} as const;

function readJson<T>(key: string): T | null {
  const raw = localStorage.getItem(key);
  return raw ? (JSON.parse(raw) as T) : null;
}

export const ACCESS_TOKEN_REFRESH_MARGIN_SECONDS = 60;

@Injectable({ providedIn: 'root' })
export class SessionStore {
  readonly accessToken = signal<string | null>(localStorage.getItem(STORAGE_KEYS.token));
  readonly customer = signal<Customer | null>(readJson<Customer>(STORAGE_KEYS.customer));
  readonly customers = signal<Customer[]>(readJson<Customer[]>(STORAGE_KEYS.customers) ?? []);
  readonly language = signal<string>(localStorage.getItem(STORAGE_KEYS.language) ?? environment.defaultLanguage);
  readonly permissions = signal<string[]>([]);

  readonly tokenPayload = computed<AccessTokenPayload | null>(() => decodeAccessToken(this.accessToken()));
  readonly isOtpRequired = computed(() => !!this.tokenPayload()?.otp_confirmation_required);
  readonly isResetPasswordRequired = computed(() => !!this.tokenPayload()?.reset_password_required);
  readonly passwordExpiresIn = computed<number | null>(() => this.tokenPayload()?.password_expires_in ?? null);
  readonly isPasswordExpiringSoon = computed(() => !!this.tokenPayload()?.password_expires_in);

  constructor() {
    window.addEventListener('storage', event => {
      if (event.key === null || event.key === STORAGE_KEYS.token) {
        this.accessToken.set(localStorage.getItem(STORAGE_KEYS.token));
      }
    });
  }
  isAuthenticated(): boolean {
    const payload = this.tokenPayload();
    if (!payload) {
      return false;
    }
    if (!payload.refresh_token_expiration) {
      return true;
    }
    return payload.refresh_token_expiration > this.nowSeconds();
  }

  isAccessTokenExpiring(marginSeconds = ACCESS_TOKEN_REFRESH_MARGIN_SECONDS): boolean {
    const payload = this.tokenPayload();
    return !!payload && payload.exp - marginSeconds <= this.nowSeconds();
  }

  isRefreshTokenExpired(): boolean {
    const payload = this.tokenPayload();
    if (!payload) {
      return true;
    }
    if (!payload.refresh_token_expiration) {
      return false;
    }
    return payload.refresh_token_expiration <= this.nowSeconds();
  }

  setAccessToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.token, token);
    this.accessToken.set(token);
  }

  setCustomer(customer: Customer): void {
    localStorage.setItem(STORAGE_KEYS.customer, JSON.stringify(customer));
    this.customer.set(customer);
  }

  setCustomers(customers: Customer[]): void {
    localStorage.setItem(STORAGE_KEYS.customers, JSON.stringify(customers));
    this.customers.set(customers);
  }

  setPermissions(permissions: string[]): void {
    this.permissions.set(permissions);
  }

  setLanguage(language: string): void {
    localStorage.setItem(STORAGE_KEYS.language, language);
    this.language.set(language);
  }

  setAuthMeans(authMeans: string): void {
    localStorage.setItem(STORAGE_KEYS.authMeans, authMeans);
  }

  getAuthMeans(): string | null {
    return localStorage.getItem(STORAGE_KEYS.authMeans);
  }

  removeAuthMeans(): void {
    localStorage.removeItem(STORAGE_KEYS.authMeans);
  }

  hasShownPasswordExpiryNotice(): boolean {
    return sessionStorage.getItem(SESSION_STORAGE_KEYS.passwordExpiryNoticeShown) === 'true';
  }

  markPasswordExpiryNoticeShown(): void {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.passwordExpiryNoticeShown, 'true');
  }

  clear(): void {
    const language = this.language();
    localStorage.clear();
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.passwordExpiryNoticeShown);
    localStorage.setItem(STORAGE_KEYS.language, language);
    this.accessToken.set(null);
    this.customer.set(null);
    this.customers.set([]);
    this.permissions.set([]);
  }

  private nowSeconds(): number {
    return Date.now() / 1000;
  }
}
